import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetFishTypes } from '@/domain/cages';
import { useGetClientOverviewWithFishGroups } from '@/domain/client';
import {
    type SiteTimelineScope,
    type TimelineFishGroup,
    useGetSiteOccupancy,
    useGetSiteTimeline,
} from '@/domain/fishGroups';
import { plannedSeasonCode, useGetStockingCycles } from '@/domain/stockingCycles';
import { now } from '@/utils/date';

import EditStockingModal from './components/EditStockingModal/EditStockingModal';
import FishGroupDetailModal, {
    type SiteActionKind,
} from './components/FishGroupDetailModal/FishGroupDetailModal';
import MergeFishGroupModal from './components/MergeFishGroupModal/MergeFishGroupModal';
import MoveFishGroupModal from './components/MoveFishGroupModal/MoveFishGroupModal';
import NewFishGroupModal from './components/NewFishGroupModal/NewFishGroupModal';
import NewStockingModal from './components/NewStockingModal/NewStockingModal';
import SiteDelousingModal from './components/SiteDelousingModal/SiteDelousingModal';
import SplitFishGroupModal from './components/SplitFishGroupModal/SplitFishGroupModal';
import AssignStockingModal from './components/StockingAssignment/AssignStockingModal';
import RemoveFromStockingModal from './components/StockingAssignment/RemoveFromStockingModal';
import FishGroupBoardView from './FishGroupBoardView';
import { boardCycles, emptyPens, resolveScope, selectableCycles } from './helpers/boardOptions';
import { groupDashboardPath } from './helpers/groupDashboardPath';
import { latestStay } from './helpers/siteActions/groupFacts';
import { sitePens } from './helpers/siteActions/sitePens';
import { groupTransfers, transferFacts } from './helpers/transferFacts';
import { useBoardDemoMask } from './hooks/useBoardDemoMask';
import { useBoardModel, useLineageExpansion } from './hooks/useBoardModel';
import type { BoardCycle } from './types';

interface FishGroupBoardContainerProps {
    clientId: number;
    locationId: number;
}

type OpenModal =
    | { kind: 'none' }
    | { kind: 'stocking' }
    | { kind: 'editStocking'; stockingCycleId: number }
    | { kind: 'fishGroup'; cageId: number | null }
    | { kind: 'detail'; fishGroupId: number }
    | { kind: SiteActionKind; fishGroupId: number }
    // The stocking dialogs keep the group (and stocking) they opened for: the write refreshes
    // the board, which may no longer show the group in its scope.
    | { kind: 'assignStocking'; fishGroupId: number; group: TimelineFishGroup }
    | {
          kind: 'unassignStocking';
          fishGroupId: number;
          group: TimelineFishGroup;
          cycle: BoardCycle;
      };

export default function FishGroupBoardContainer(props: FishGroupBoardContainerProps) {
    const { clientId, locationId } = props;
    const cyclesQuery = useGetStockingCycles(clientId, locationId);
    const overview = useGetClientOverviewWithFishGroups(clientId);
    const { data: fishTypes } = useGetFishTypes();
    const [pickedScope, setPickedScope] = useState<SiteTimelineScope | null>(null);
    const [modal, setModalState] = useState<OpenModal>({ kind: 'none' });
    // What a dialog may offer depends on the real time it opens at, not on the loaded
    // timeline's generatedAt: a planned group whose start passed while the page stayed open
    // can be acted on without a refresh. The board's today line stays on the timeline.
    const [actionNow, setActionNow] = useState(now);
    const setModal = (next: OpenModal) => {
        setActionNow(now());
        setModalState(next);
    };

    // Demo mode masks display names only. Real names stay in the data the modals compute
    // with (Suggest, duplicate checks, request bodies), and every id stays real.
    const mask = useBoardDemoMask();
    const allCycles: BoardCycle[] = useMemo(
        () =>
            (cyclesQuery.data?.cycles ?? []).map((cycle) => ({
                ...cycle,
                displayName: mask.cycleName(cycle.id, cycle.name),
            })),
        [mask, cyclesQuery.data]
    );
    const shownCycles = useMemo(() => boardCycles(allCycles), [allCycles]);
    // A new group only goes into a planned or active cycle.
    const openCycles = useMemo(() => selectableCycles(allCycles), [allCycles]);
    const unassignedCount = cyclesQuery.data?.unassignedGroupCount ?? 0;
    const selectedScope = resolveScope(pickedScope, allCycles, unassignedCount);
    const selectedCycle = shownCycles.find((cycle) => cycle.id === selectedScope) ?? null;
    const createCycleId = openCycles.some((cycle) => cycle.id === selectedCycle?.id)
        ? (selectedCycle?.id ?? null)
        : (openCycles[0]?.id ?? null);

    const timelineQuery = useGetSiteTimeline(clientId, locationId, selectedScope);
    const boardTimeline = useMemo(
        () => mask.timeline(timelineQuery.data),
        [mask, timelineQuery.data]
    );
    // Off until design review: the board reads one row per group unless a user combines them.
    const [combineChains, setCombineChains] = useState(false);
    const board = useBoardModel(boardTimeline, combineChains);
    const expansion = useLineageExpansion(board?.blocks ?? []);

    const location =
        overview.data?.locations.find((candidate) => candidate.locationId === locationId) ?? null;
    const locationName = mask.locationName(locationId, location?.locationName ?? '');
    // Site-wide, not the selected scope: a pen another cycle holds isn't free either. Until
    // that is known, or if it can't be, no pen is offered: any of them could be taken.
    const occupancy = useGetSiteOccupancy(clientId, locationId);
    // The overview lists the site's pens; without it no pen can be offered either.
    const pensFailed = occupancy.error !== null || overview.error !== null;
    const occupancyKnown = !occupancy.isLoading && !pensFailed;
    const pens = useMemo(
        () => (occupancyKnown ? emptyPens(location, occupancy.occupiedCageIds, mask.penName) : []),
        [occupancyKnown, location, occupancy.occupiedCageIds, mask]
    );
    const retryPens = () => {
        overview.refetch();
        occupancy.refetch();
    };
    // Live and closed groups alike, so Suggest never repeats a name used at the site.
    const siteGroupNames = useMemo(
        () => [
            ...occupancy.groupNames,
            ...(location?.closedFishGroups ?? []).map((row) => row.fishGroupName),
        ],
        [occupancy.groupNames, location]
    );
    const closeModal = () => setModal({ kind: 'none' });
    const editedCycle =
        modal.kind === 'editStocking'
            ? (allCycles.find((cycle) => cycle.id === modal.stockingCycleId) ?? null)
            : null;
    // Pins the board to what it shows now until the dialog is done: a stocking created inline
    // refreshes the cycle list, and without a pick the default scope would move to it.
    const openStockingDialog = (kind: 'assign' | 'unassign', fishGroupId: number) => {
        const group = timelineQuery.data?.groups.find((candidate) => candidate.id === fishGroupId);
        if (!group) return;
        setPickedScope(selectedScope);
        const cycle = allCycles.find((candidate) => candidate.id === group.stockingCycleId);
        if (kind === 'assign') setModal({ kind: 'assignStocking', fishGroupId, group });
        else if (cycle) setModal({ kind: 'unassignStocking', fishGroupId, group, cycle });
    };

    const { pathname, search } = useLocation();
    // The action dialogs never mount in demo mode, so they get the real names.
    const actionGroup =
        modal.kind !== 'none' && 'fishGroupId' in modal && modal.kind !== 'detail'
            ? (timelineQuery.data?.groups.find((group) => group.id === modal.fishGroupId) ?? null)
            : null;
    const actionCycle = allCycles.find((cycle) => cycle.id === actionGroup?.stockingCycleId);
    const actionSeason = plannedSeasonCode(actionCycle?.plannedStartDate ?? null);
    // As for the create form: until occupancy is known, no pen is offered as empty.
    const pensOfSite = useMemo(
        () => (occupancyKnown ? sitePens(location, occupancy.pens, mask.penName) : []),
        [occupancyKnown, location, occupancy.pens, mask]
    );
    const pensState = {
        loading: overview.isLoading || occupancy.isLoading,
        failed: pensFailed,
        onRetry: retryPens,
    };
    // The detail dialog shows the group as the board does, masked in demo mode.
    const detailGroup =
        modal.kind === 'detail'
            ? (boardTimeline?.groups.find((group) => group.id === modal.fishGroupId) ?? null)
            : null;
    const facts = useMemo(
        () => (boardTimeline ? transferFacts(boardTimeline) : []),
        [boardTimeline]
    );
    const detailCycleName =
        allCycles.find((cycle) => cycle.id === detailGroup?.stockingCycleId)?.displayName ?? null;
    const detailPenId = detailGroup ? (latestStay(detailGroup)?.cageId ?? null) : null;
    const dashboardPath =
        detailGroup && detailPenId !== null
            ? groupDashboardPath(pathname, search, detailPenId, detailGroup.id)
            : null;

    return (
        <>
            <FishGroupBoardView
                locationName={locationName}
                cycles={shownCycles}
                unassignedCount={unassignedCount}
                selectedScope={selectedScope}
                selectedCycle={selectedCycle}
                loading={cyclesQuery.isLoading || timelineQuery.isLoading}
                error={cyclesQuery.error ?? timelineQuery.error}
                board={board}
                emptyPens={pens}
                showPrevious={expansion.showPrevious}
                expandedBlocks={expansion.expandedBlocks}
                onShowPreviousChange={expansion.setShowPrevious}
                onToggleBlock={expansion.toggleBlock}
                collapsedRows={expansion.collapsedRows}
                highlightedRow={expansion.highlightedRow}
                onToggleOrigin={expansion.toggleOrigin}
                lineageNotice={expansion.notice}
                combineChains={combineChains}
                onCombineChainsChange={(value) => {
                    setCombineChains(value);
                    // Rows are keyed by their latest group, which the other view doesn't share.
                    expansion.setShowPrevious(expansion.showPrevious);
                }}
                onSelectScope={setPickedScope}
                onNewStocking={() => setModal({ kind: 'stocking' })}
                onNewFishGroup={(cageId) => setModal({ kind: 'fishGroup', cageId })}
                onOpenGroup={(fishGroupId) => setModal({ kind: 'detail', fishGroupId })}
                onEditStocking={(stockingCycleId) => {
                    // The board stays on the stocking through the save and its refetch.
                    setPickedScope(stockingCycleId);
                    setModal({ kind: 'editStocking', stockingCycleId });
                }}
                writesDisabled={mask.writesDisabled}
            />
            {detailGroup && (
                <FishGroupDetailModal
                    group={detailGroup}
                    cycleName={detailCycleName}
                    locationName={locationName}
                    now={actionNow}
                    dashboardPath={dashboardPath}
                    writesDisabled={mask.writesDisabled}
                    transfers={groupTransfers(facts, detailGroup.id)}
                    onAction={(kind) => setModal({ kind, fishGroupId: detailGroup.id })}
                    onStockingAction={(kind) => openStockingDialog(kind, detailGroup.id)}
                    onClose={closeModal}
                />
            )}
            {!mask.writesDisabled && actionGroup && modal.kind === 'merge' && (
                <MergeFishGroupModal
                    clientId={clientId}
                    locationId={locationId}
                    group={actionGroup}
                    pens={pensOfSite}
                    pensState={pensState}
                    season={actionSeason}
                    siteNames={siteGroupNames}
                    cycleName={(cycleId) =>
                        allCycles.find((cycle) => cycle.id === cycleId)?.name ?? ''
                    }
                    now={actionNow}
                    onSwitch={(kind) => setModal({ kind, fishGroupId: actionGroup.id })}
                    onClose={() => setModal({ kind: 'detail', fishGroupId: actionGroup.id })}
                    onDone={closeModal}
                />
            )}
            {!mask.writesDisabled && actionGroup && modal.kind === 'move' && (
                <MoveFishGroupModal
                    clientId={clientId}
                    locationId={locationId}
                    group={actionGroup}
                    pens={pensOfSite}
                    pensState={pensState}
                    onSwitch={(kind) => setModal({ kind, fishGroupId: actionGroup.id })}
                    onClose={() => setModal({ kind: 'detail', fishGroupId: actionGroup.id })}
                    onDone={closeModal}
                />
            )}
            {!mask.writesDisabled &&
                actionGroup &&
                (modal.kind === 'split' || modal.kind === 'grading') && (
                    <SplitFishGroupModal
                        clientId={clientId}
                        locationId={locationId}
                        group={actionGroup}
                        pens={pensOfSite}
                        pensState={pensState}
                        season={actionSeason}
                        siteNames={siteGroupNames}
                        grading={modal.kind === 'grading'}
                        onClose={() => setModal({ kind: 'detail', fishGroupId: actionGroup.id })}
                        onDone={closeModal}
                    />
                )}
            {!mask.writesDisabled && actionGroup && modal.kind === 'delousing' && (
                <SiteDelousingModal
                    clientId={clientId}
                    locationId={locationId}
                    group={actionGroup}
                    pens={pensOfSite}
                    pensState={pensState}
                    onClose={() => setModal({ kind: 'detail', fishGroupId: actionGroup.id })}
                    onDone={closeModal}
                />
            )}
            {!mask.writesDisabled && modal.kind === 'assignStocking' && (
                <AssignStockingModal
                    clientId={clientId}
                    locationId={locationId}
                    group={modal.group}
                    cycles={shownCycles}
                    locationName={locationName}
                    onClose={() => setModal({ kind: 'detail', fishGroupId: modal.fishGroupId })}
                    onDone={(cycleId) => {
                        setPickedScope(cycleId);
                        closeModal();
                    }}
                />
            )}
            {!mask.writesDisabled && modal.kind === 'unassignStocking' && (
                <RemoveFromStockingModal
                    clientId={clientId}
                    locationId={locationId}
                    group={modal.group}
                    cycle={modal.cycle}
                    cycles={shownCycles}
                    onClose={() => setModal({ kind: 'detail', fishGroupId: modal.fishGroupId })}
                    onDone={() => {
                        setPickedScope('unassigned');
                        closeModal();
                    }}
                />
            )}
            {!mask.writesDisabled && editedCycle && (
                <EditStockingModal
                    clientId={clientId}
                    locationId={locationId}
                    cycle={editedCycle}
                    cycles={allCycles}
                    onClose={closeModal}
                    onSaved={closeModal}
                />
            )}
            {/* Never mounted in demo mode, so no create form can show or save a name. */}
            {!mask.writesDisabled && modal.kind === 'stocking' && (
                <NewStockingModal
                    clientId={clientId}
                    locationId={locationId}
                    locationName={locationName}
                    cycles={allCycles}
                    suggestedName={cyclesQuery.data?.suggestedName ?? null}
                    isOpen
                    onClose={closeModal}
                    onCreated={(cycleId) => {
                        if (cycleId !== null) setPickedScope(cycleId);
                        closeModal();
                    }}
                />
            )}
            {!mask.writesDisabled && modal.kind === 'fishGroup' && (
                <NewFishGroupModal
                    clientId={clientId}
                    locationId={locationId}
                    cycles={openCycles}
                    defaultCycleId={createCycleId}
                    pens={pens}
                    pensLoading={overview.isLoading || occupancy.isLoading}
                    pensError={pensFailed}
                    onRetryPens={retryPens}
                    defaultCageId={modal.cageId}
                    existingNames={siteGroupNames}
                    fishTypes={fishTypes}
                    isOpen
                    onClose={closeModal}
                    onCreated={(cycleId) => {
                        setPickedScope(cycleId);
                        closeModal();
                    }}
                />
            )}
        </>
    );
}
